/**
 * Cache Monitoring Service
 * 
 * Production-grade monitoring and observability for the Phase 4 cache invalidation system.
 * Provides metrics collection, error tracking, and performance monitoring for cache operations.
 * 
 * Phase 5 implementation - production monitoring and observability
 */

import { detectCommunityContext } from './communityAwareCacheInvalidation';
import { getNamespaceKeys } from './namespacedStorageService';

// Development/production environment detection
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Cache operation types for monitoring
 */
export type CacheOperationType = 
  | 'invalidation'
  | 'migration'
  | 'selective_clear'
  | 'namespace_clear'
  | 'atomic_reset'
  | 'storage_read'
  | 'storage_write'
  | 'ttl_expiration';

/**
 * Cache monitoring event structure
 */
export interface CacheMonitoringEvent {
  id: string;
  type: CacheOperationType;
  timestamp: number;
  duration?: number;
  success: boolean;
  context: {
    environment: string;
    community?: string;
    metaVersion?: string;
    userAgent?: string;
    sessionId?: string;
  };
  metrics?: {
    keysAffected?: number;
    keysPreserved?: number;
    storageUsage?: number;
    memoryUsage?: number;
  };
  error?: {
    message: string;
    stack?: string;
    code?: string;
  };
  metadata?: Record<string, any>;
}

/**
 * Cache health metrics
 */
export interface CacheHealthMetrics {
  totalKeys: number;
  namespacedKeys: number;
  legacyKeys: number;
  expiredKeys: number;
  storageUsage: number;
  averageKeyAge: number;
  migrationStatus: 'completed' | 'pending' | 'failed';
  errorRate: number;
  performanceScore: number;
  lastHealthCheck: number;
}

/**
 * Performance thresholds for monitoring
 */
const PERFORMANCE_THRESHOLDS = {
  invalidation: {
    warning: 1000, // 1 second
    critical: 5000  // 5 seconds
  },
  migration: {
    warning: 2000, // 2 seconds
    critical: 10000 // 10 seconds
  },
  storage_operation: {
    warning: 100,  // 100ms
    critical: 500  // 500ms
  }
} as const;

/**
 * Monitoring data storage (in-memory with persistence backup)
 */
class CacheMonitoringStore {
  private events: CacheMonitoringEvent[] = [];
  private metrics: Partial<CacheHealthMetrics> = {};
  private readonly maxEvents = 1000; // Keep last 1000 events
  private readonly sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  /**
   * Add a monitoring event
   */
  addEvent(event: Omit<CacheMonitoringEvent, 'id' | 'timestamp' | 'context'>): CacheMonitoringEvent {
    const communityContext = detectCommunityContext();
    
    const fullEvent: CacheMonitoringEvent = {
      ...event,
      id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      context: {
        environment: isDevelopment ? 'dev' : 'prod',
        community: communityContext.hostname,
        metaVersion: event.metadata?.metaVersion,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        sessionId: this.sessionId,
        ...event.metadata?.context
      }
    };

    this.events.push(fullEvent);
    
    // Maintain event limit
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Persist critical events in development
    if (isDevelopment && (!event.success || event.duration && event.duration > 1000)) {
      this.persistEvent(fullEvent);
    }

    return fullEvent;
  }

  /**
   * Get recent events
   */
  getRecentEvents(limit: number = 50): CacheMonitoringEvent[] {
    return this.events.slice(-limit);
  }

  /**
   * Get events by type
   */
  getEventsByType(type: CacheOperationType, limit: number = 50): CacheMonitoringEvent[] {
    return this.events
      .filter(event => event.type === type)
      .slice(-limit);
  }

  /**
   * Update health metrics
   */
  updateMetrics(metrics: Partial<CacheHealthMetrics>): void {
    this.metrics = { ...this.metrics, ...metrics, lastHealthCheck: Date.now() };
  }

  /**
   * Get current health metrics
   */
  getMetrics(): Partial<CacheHealthMetrics> {
    return { ...this.metrics };
  }

  /**
   * Calculate error rate
   */
  getErrorRate(timeWindowMs: number = 60000): number {
    const cutoff = Date.now() - timeWindowMs;
    const recentEvents = this.events.filter(event => event.timestamp > cutoff);
    
    if (recentEvents.length === 0) return 0;
    
    const failures = recentEvents.filter(event => !event.success).length;
    return (failures / recentEvents.length) * 100;
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats(type?: CacheOperationType, timeWindowMs: number = 300000): {
    averageDuration: number;
    p95Duration: number;
    operationCount: number;
  } {
    const cutoff = Date.now() - timeWindowMs;
    let events = this.events.filter(event => 
      event.timestamp > cutoff && 
      event.duration !== undefined
    );

    if (type) {
      events = events.filter(event => event.type === type);
    }

    if (events.length === 0) {
      return { averageDuration: 0, p95Duration: 0, operationCount: 0 };
    }

    const durations = events.map(event => event.duration!).sort((a, b) => a - b);
    const averageDuration = durations.reduce((sum, duration) => sum + duration, 0) / durations.length;
    const p95Index = Math.floor(durations.length * 0.95);
    const p95Duration = durations[p95Index] || durations[durations.length - 1];

    return {
      averageDuration,
      p95Duration,
      operationCount: events.length
    };
  }

  /**
   * Persist critical event to localStorage for debugging
   */
  private persistEvent(event: CacheMonitoringEvent): void {
    try {
      const key = `terrarium:monitoring:critical_event:${event.id}`;
      localStorage.setItem(key, JSON.stringify({
        ...event,
        persisted: true,
        persistedAt: Date.now()
      }));
    } catch (error) {
      console.warn('CacheMonitoring: Failed to persist critical event:', error);
    }
  }
}

// Global monitoring store instance
const monitoringStore = new CacheMonitoringStore();

/**
 * Track a cache operation with performance monitoring
 */
export async function trackCacheOperation<T>(
  type: CacheOperationType,
  operation: () => Promise<T> | T,
  metadata?: Record<string, any>
): Promise<T> {
  const startTime = performance.now();
  const operationId = `${type}_${Date.now()}`;

  if (isDevelopment) {
    performance.mark(`${operationId}_start`);
  }

  try {
    const result = await operation();
    const duration = performance.now() - startTime;

    // Log successful operation
    const event = monitoringStore.addEvent({
      type,
      duration,
      success: true,
      metadata: {
        ...metadata,
        operationId,
        result: typeof result === 'object' && result !== null ? 
          { type: 'object', keys: Object.keys(result).length } : 
          { type: typeof result, value: String(result).substring(0, 100) }
      }
    });

    // Performance warnings
    const threshold = PERFORMANCE_THRESHOLDS[type as keyof typeof PERFORMANCE_THRESHOLDS] || 
                     PERFORMANCE_THRESHOLDS.storage_operation;

    if (duration > threshold.critical) {
      console.error(`CacheMonitoring: CRITICAL performance issue with ${type}:`, {
        duration: duration.toFixed(2) + 'ms',
        threshold: threshold.critical + 'ms',
        eventId: event.id
      });
    } else if (duration > threshold.warning) {
      console.warn(`CacheMonitoring: Performance warning for ${type}:`, {
        duration: duration.toFixed(2) + 'ms',
        threshold: threshold.warning + 'ms',
        eventId: event.id
      });
    }

    if (isDevelopment) {
      performance.measure(`cache_${operationId}`, `${operationId}_start`);
      console.log(`CacheMonitoring: ${type} completed in ${duration.toFixed(2)}ms`, {
        eventId: event.id,
        metadata
      });
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    // Log failed operation
    const event = monitoringStore.addEvent({
      type,
      duration,
      success: false,
      error: {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        code: (error as any)?.code
      },
      metadata: {
        ...metadata,
        operationId
      }
    });

    console.error(`CacheMonitoring: ${type} failed after ${duration.toFixed(2)}ms:`, {
      error: error instanceof Error ? error.message : error,
      eventId: event.id,
      metadata
    });

    throw error;
  }
}

/**
 * Collect current cache health metrics
 */
export async function collectCacheHealthMetrics(): Promise<CacheHealthMetrics> {
  return trackCacheOperation('storage_read', async () => {
    try {
      const allKeys = Object.keys(localStorage);
      const namespacedKeys = allKeys.filter(key => key.startsWith('terrarium:'));
      const legacyKeys = allKeys.filter(key => 
        key.startsWith('terrarium_') && !key.startsWith('terrarium:')
      );

      // Calculate storage usage
      let storageUsage = 0;
      allKeys.forEach(key => {
        try {
          const value = localStorage.getItem(key) || '';
          storageUsage += key.length + value.length;
        } catch (error) {
          // Skip keys that can't be read
        }
      });

      // Get namespace-specific metrics
      const devKeysResult = getNamespaceKeys({ env: 'dev' });
      const prodKeysResult = getNamespaceKeys({ env: 'prod' });
      
      const totalNamespacedKeys = [
        ...(devKeysResult.success ? devKeysResult.data || [] : []),
        ...(prodKeysResult.success ? prodKeysResult.data || [] : [])
      ].length;

      // Calculate error rate
      const errorRate = monitoringStore.getErrorRate();
      
      // Simple performance score (0-100)
      const performanceStats = monitoringStore.getPerformanceStats();
      const avgDuration = performanceStats.averageDuration;
      const performanceScore = Math.max(0, Math.min(100, 
        100 - (avgDuration / 50) // 50ms = 100 score, 5000ms = 0 score
      ));

      const metrics: CacheHealthMetrics = {
        totalKeys: allKeys.length,
        namespacedKeys: totalNamespacedKeys,
        legacyKeys: legacyKeys.length,
        expiredKeys: 0, // Would need to check TTL, simplified for now
        storageUsage,
        averageKeyAge: 0, // Would need creation timestamps
        migrationStatus: legacyKeys.length === 0 ? 'completed' : 'pending',
        errorRate,
        performanceScore,
        lastHealthCheck: Date.now()
      };

      monitoringStore.updateMetrics(metrics);
      return metrics;
    } catch (error) {
      console.error('CacheMonitoring: Failed to collect health metrics:', error);
      throw error;
    }
  }, { source: 'health_check' });
}

/**
 * Get monitoring dashboard data
 */
export function getMonitoringDashboard() {
  const recentEvents = monitoringStore.getRecentEvents(100);
  const errorRate = monitoringStore.getErrorRate();
  const performanceStats = monitoringStore.getPerformanceStats();
  const metrics = monitoringStore.getMetrics();

  return {
    summary: {
      totalEvents: recentEvents.length,
      errorRate,
      ...performanceStats,
      ...metrics
    },
    recentEvents: recentEvents.slice(-20),
    performanceByType: {
      invalidation: monitoringStore.getPerformanceStats('invalidation'),
      migration: monitoringStore.getPerformanceStats('migration'),
      selective_clear: monitoringStore.getPerformanceStats('selective_clear'),
      atomic_reset: monitoringStore.getPerformanceStats('atomic_reset')
    },
    errorEvents: recentEvents
      .filter(event => !event.success)
      .slice(-10)
  };
}

/**
 * Development utilities for monitoring
 */
export const devMonitoringUtils = isDevelopment ? {
  /**
   * Clear all monitoring data
   */
  clearMonitoringData(): void {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith('terrarium:monitoring:')
    );
    keys.forEach(key => localStorage.removeItem(key));
    console.log(`CacheMonitoring: Cleared ${keys.length} monitoring keys`);
  },

  /**
   * Export monitoring data for analysis
   */
  exportMonitoringData(): string {
    const dashboard = getMonitoringDashboard();
    return JSON.stringify(dashboard, null, 2);
  },

  /**
   * Simulate a performance issue for testing
   */
  simulatePerformanceIssue(): Promise<void> {
    return trackCacheOperation('invalidation', async () => {
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
      console.log('Simulated performance issue completed');
    }, { simulated: true });
  },

  /**
   * Simulate an error for testing
   */
  simulateError(): Promise<void> {
    return trackCacheOperation('migration', async () => {
      throw new Error('Simulated error for testing monitoring');
    }, { simulated: true });
  }
} : {};

/**
 * Initialize monitoring system
 */
export function initializeCacheMonitoring(): void {
  if (isDevelopment) {
    console.log('CacheMonitoring: Initializing monitoring system');
    
    // Collect initial metrics
    collectCacheHealthMetrics().catch(error => {
      console.warn('CacheMonitoring: Initial health check failed:', error);
    });

    // Set up periodic health checks in development
    setInterval(() => {
      collectCacheHealthMetrics().catch(error => {
        console.warn('CacheMonitoring: Periodic health check failed:', error);
      });
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  // Set up error tracking
  window.addEventListener('error', (event) => {
    if (event.error && event.error.message?.includes('cache') || 
        event.error.message?.includes('storage')) {
      monitoringStore.addEvent({
        type: 'storage_read',
        success: false,
        error: {
          message: event.error.message,
          stack: event.error.stack
        },
        metadata: {
          source: 'global_error_handler',
          filename: event.filename,
          lineno: event.lineno
        }
      });
    }
  });
}

// Auto-initialize in development
if (isDevelopment && typeof window !== 'undefined') {
  initializeCacheMonitoring();
}